import React, { useState, useEffect, useRef } from "react";
import { useSwipeable } from "react-swipeable";
import { FiChevronDown, FiRotateCcw, FiCheck, FiX } from "react-icons/fi";
import CachedImage from "./ui/CachedImage";
import Dropdown from "./ui/Dropdown";
import { getTeamHelmetUrl } from "../utils/helmetGenerator";
import { formatGameDate, getWeekBoundaries } from "../utils/dateUtils";

// Animation constants
const ANIMATION_DURATION = 300;
const CARD_MOVE_DURATION = 200;
const PICK_ANIMATION_DELAY = 500;
// Real "did the user mean it" threshold, distinct from react-swipeable's own
// small gesture-detection delta below, so a light touch doesn't submit a pick.
const SWIPE_THRESHOLD_PERCENTAGE = 0.2;
// How much of each px of finger movement the card actually travels — higher
// than before so the card tracks the finger closely, Tinder-style.
const DRAG_FOLLOW_FACTOR = 0.5;

const CardBasedMobilePicks = ({
  games,
  userPicks,
  onMakePick,
  currentWeek,
  onWeekChange,
  weeks,
  title,
}) => {
  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  // Card animation states
  const [cardRotation, setCardRotation] = useState(0);
  const [cardTranslateX, setCardTranslateX] = useState(0);
  const [cardTranslateY, setCardTranslateY] = useState(0);
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Refs
  const cardRef = useRef(null);

  // Find first unpicked game or first game
  useEffect(() => {
    const firstUnpickedIndex = games.findIndex(
      (game) => game.status === "scheduled" && !userPicks[game.id],
    );
    setCurrentGameIndex(firstUnpickedIndex >= 0 ? firstUnpickedIndex : 0);
  }, [games, userPicks]);

  // Check if all games have been picked
  useEffect(() => {
    const allPicked = games.every(
      (game) => game.status !== "scheduled" || userPicks[game.id],
    );
    if (allPicked && games.length > 0) {
      setShowSummary(true);
    }
  }, [games, userPicks]);

  // Calculate pick counts
  const pickCounts = {
    made: Object.keys(userPicks).length,
    total: games.filter(g => g.status === "scheduled").length,
  };

  // Determine if a pick is correct for completed games
  const getPickCorrectness = (game, userPick) => {
    if (!userPick || game.status !== "completed") return null;
    return userPick.isCorrect;
  };

  // Determine if this is a past week for header text
  const isPastWeek = () => {
    if (!currentWeek) return false;
    const now = new Date();
    const seasonYear = now.getFullYear();
    const { weekStart } = getWeekBoundaries(currentWeek, seasonYear);
    return weekStart < now;
  };

  // Handle pick selection
  const handlePick = async (teamToSelect) => {
    if (isAnimating || selectedTeam) return;
    if (games[currentGameIndex]?.status !== "scheduled") return;

    setIsAnimating(true);
    setSelectedTeam(teamToSelect);

    try {
      await onMakePick(games[currentGameIndex].id, teamToSelect);

      // Fly off animation
      const direction = teamToSelect === games[currentGameIndex].homeTeam.abbreviation ? 1 : -1;
      setCardTranslateX(direction * window.innerWidth);
      setCardRotation(direction * 20);

      // Move to next game after animation
      setTimeout(() => {
        moveToNextGame();
      }, ANIMATION_DURATION);

    } catch (error) {
      console.error("Error submitting pick:", error);
      // Reset on error
      setSelectedTeam(null);
      setIsAnimating(false);
      snapBackToCenter();
    }
  };

  // Commit or reject a released swipe against the real distance threshold —
  // the single place a swipe turns into a pick (or snaps back if too short).
  const commitSwipe = (eventData, teamToSelect) => {
    if (isAnimating || selectedTeam) return;

    const screenWidth = window.innerWidth;
    const threshold = screenWidth * SWIPE_THRESHOLD_PERCENTAGE;

    if (eventData.absX < threshold) {
      // Didn't drag far enough to count as an intentional pick — snap back.
      setCardRotation(0);
      setCardTranslateX(0);
      return;
    }

    handlePick(teamToSelect);
  };

  // Single source of truth for the swipe gesture: live drag-follow visual via
  // onSwiping, commit-or-snap-back on release. (Previously a second, manual
  // onTouchStart/Move/End system was also bound to this same card and could
  // independently fire a pick for the same physical swipe.)
  const cardSwipeHandlers = useSwipeable({
    onSwiping: (eventData) => {
      if (isAnimating || selectedTeam) return;
      if (games[currentGameIndex]?.status !== "scheduled") return;
      if (Math.abs(eventData.deltaX) <= Math.abs(eventData.deltaY)) return;

      const screenWidth = window.innerWidth;
      const rotation = Math.max(-18, Math.min(18, (eventData.deltaX / screenWidth) * 40));
      setCardRotation(rotation);
      setCardTranslateX(eventData.deltaX * DRAG_FOLLOW_FACTOR);
    },
    onSwipedLeft: (eventData) =>
      commitSwipe(eventData, games[currentGameIndex].awayTeam.abbreviation),
    onSwipedRight: (eventData) =>
      commitSwipe(eventData, games[currentGameIndex].homeTeam.abbreviation),
    // Fires on EVERY release, unlike onSwiped/onSwipedLeft/Right which
    // react-swipeable suppresses when the gesture is vertical or outruns
    // swipeDuration — without this a slow drag leaves the card stuck rotated
    // mid-screen. Runs after the onSwipedLeft/Right above, so a committed pick
    // (which sets selectedTeam) is never snapped back.
    onTouchEndOrOnMouseUp: () => {
      if (!isAnimating && !selectedTeam) {
        setCardRotation(0);
        setCardTranslateX(0);
      }
    },
    trackMouse: false,
    delta: 10,
    // No swipeDuration cap: a slow, deliberate drag is still a real pick.
    trackTouch: true,
  });

  // Snap back animation
  const snapBackToCenter = () => {
    setIsAnimating(true);
    setCardRotation(0);
    setCardTranslateX(0);
    setSelectedTeam(null);

    setTimeout(() => {
      setIsAnimating(false);
    }, PICK_ANIMATION_DELAY);
  };

  // Move to next game
  const moveToNextGame = () => {
    const nextUnpickedIndex = games.findIndex(
      (game, index) =>
        index > currentGameIndex &&
        game.status === "scheduled" &&
        !userPicks[game.id],
    );

    if (nextUnpickedIndex >= 0) {
      setCurrentGameIndex(nextUnpickedIndex);
    } else if (currentGameIndex < games.length - 1) {
      setCurrentGameIndex((prev) => prev + 1);
    }

    // Reset card state
    setSelectedTeam(null);
    setCardRotation(0);
    setCardTranslateX(0);
    setIsAnimating(false);
  };

  // Handle skip game
  const handleSkipGame = () => {
    if (isAnimating) return;

    setIsAnimating(true);
    setCardTranslateY(50);
    setCardTranslateX(0);
    setCardRotation(0);

    setTimeout(() => {
      moveToNextGame();
      setCardTranslateY(0);
      setIsAnimating(false);
    }, CARD_MOVE_DURATION);
  };

  // Handle go back to previous game (undo functionality)
  const handleGoBack = () => {
    if (isAnimating || currentGameIndex === 0) return;

    setIsAnimating(true);
    setCardTranslateY(-50);
    setCardTranslateX(0);
    setCardRotation(0);

    setTimeout(() => {
      setCurrentGameIndex(prev => prev - 1);
      setSelectedTeam(null);
      setCardTranslateY(0);
      setIsAnimating(false);
    }, CARD_MOVE_DURATION);
  };

  // Jump straight into a specific game's card (used by the top progress
  // strip and by tapping a row in the summary screen to edit that pick).
  const goToGame = (index) => {
    if (!isAnimating) {
      setCurrentGameIndex(index);
    }
  };

  if (showSummary) {
    return (
      <div className="h-full flex flex-col overflow-hidden bg-gray-50">
        {/* App Banner */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 flex-shrink-0">
          <div className="text-center">
            <h1 className="text-lg font-bold">All Picks Submitted!</h1>
            <div className="text-sm opacity-90 mt-1">
              {pickCounts.made} of {pickCounts.total} games picked
            </div>
          </div>
        </div>

        {/* Summary Content.
            `my-auto` on the inner block rather than `justify-center` on the
            scroller: flex centering makes overflowing content unreachable
            above the top of the scroll range, so the heading could never be
            scrolled back to. Auto margins collapse when content overflows. */}
        <div
          className="flex-1 min-h-0 flex flex-col items-center p-6 bg-gray-50 overflow-y-auto"
          style={{ paddingBottom: 'calc(2rem + var(--chrome-inset-bottom, 0px) + env(safe-area-inset-bottom))' }}
        >
          <div className="text-center my-auto">
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              All Picks Submitted!
            </h2>
            <p className="text-gray-600 mb-6">
              You&apos;ve successfully made picks for all {games.length} games
              this week.
            </p>

            <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Your Picks Summary
              </h3>
              <p className="text-xs text-gray-500 -mt-2 mb-4">
                Tap any game to change your pick
              </p>
              <div className="space-y-3">
                {games
                  .filter((game) => userPicks[game.id])
                  .map((game) => (
                    <button
                      key={game.id}
                      type="button"
                      onClick={() => {
                        const idx = games.findIndex((g) => g.id === game.id);
                        if (idx >= 0) {
                          goToGame(idx);
                          setShowSummary(false);
                        }
                      }}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded transition-colors text-left touch-manipulation"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8">
                          <CachedImage
                            src={
                              game.awayTeam.logoUrl ||
                              getTeamHelmetUrl(game.awayTeam)
                            }
                            fallbackSrc={getTeamHelmetUrl(game.awayTeam)}
                            alt={game.awayTeam.name}
                            className="w-full h-full"
                          />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {game.awayTeam.abbreviation} @{" "}
                            {game.homeTeam.abbreviation}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatGameDate(game.gameDate, false)}
                          </div>
                        </div>
                        <div className="w-8 h-8">
                          <CachedImage
                            src={
                              game.homeTeam.logoUrl ||
                              getTeamHelmetUrl(game.homeTeam)
                            }
                            fallbackSrc={getTeamHelmetUrl(game.homeTeam)}
                            alt={game.homeTeam.name}
                            className="w-full h-full"
                          />
                        </div>
                      </div>
                      <div className="text-green-600 font-semibold">
                        {userPicks[game.id]?.selectedTeam?.abbreviation}
                      </div>
                    </button>
                  ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowSummary(false)}
                className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
              >
                Edit Picks
              </button>
              <button
                onClick={() => (window.location.href = "/leaderboard")}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                View Leaderboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-500 bg-gray-50">
        <div className="text-6xl mb-4">🏈</div>
        <p className="text-lg">No games available</p>
        <p className="text-sm">Check back later for upcoming games</p>
      </div>
    );
  }

  const currentGame = games[currentGameIndex];
  const existingPickTeam =
    userPicks[currentGame.id]?.selectedTeam?.abbreviation ?? null;
  const highlightedTeam = selectedTeam || existingPickTeam;
  const canPickCurrent = currentGame.status === "scheduled";

  // Drag "stamp" feedback — mirrors the card's own translateX so it moves
  // and rotates with the card, at full strength right as the drag crosses
  // the real commit threshold (same math as commitSwipe's threshold check).
  const dragStampMaxPx = window.innerWidth * SWIPE_THRESHOLD_PERCENTAGE * DRAG_FOLLOW_FACTOR;
  const dragStampStrength = Math.min(Math.abs(cardTranslateX) / dragStampMaxPx, 1);
  const showAwayStamp = canPickCurrent && cardTranslateX < -2;
  const showHomeStamp = canPickCurrent && cardTranslateX > 2;

  return (
    <div
      // h-full, not 100dvh: Layout already sizes the viewport and reserves the
      // app banner, and applies safe-area padding — doing either again here
      // would overflow the screen.
      className="h-full flex flex-col overflow-hidden bg-slate-900"
      style={{ overscrollBehavior: 'contain' }}
    >
      {/* Slim top bar */}
      <div className="flex-shrink-0 px-4 pt-3 pb-2">
        <div className="flex items-center justify-between gap-3 mb-2">
          <h1 className="text-white font-semibold text-base truncate">
            {title || (isPastWeek() ? "Review Your Picks" : "Make Your Picks")}
          </h1>
          <div className="text-white/60 text-xs font-medium flex-shrink-0">
            {currentWeek ? `Wk ${currentWeek} · ` : ""}
            {pickCounts.made}/{pickCounts.total}
          </div>
        </div>

        {/* Compact per-game progress strip — tap any segment to jump there */}
        <div className="flex gap-1">
          {games.map((game, index) => {
            const userPick = userPicks[game.id];
            const isCorrect = getPickCorrectness(game, userPick);
            return (
              <button
                key={game.id}
                type="button"
                onClick={() => goToGame(index)}
                aria-label={`Game ${index + 1} of ${games.length}`}
                className={`h-1.5 flex-1 rounded-full transition-colors touch-manipulation ${
                  index === currentGameIndex
                    ? "bg-white"
                    : userPick
                      ? isCorrect === false
                        ? "bg-red-400"
                        : "bg-green-400"
                      : "bg-white/25"
                }`}
              />
            );
          })}
        </div>

        {/* Week Selector — only shown when the caller provides weeks to pick from */}
        {weeks?.length > 0 && onWeekChange && (
          <div className="mt-2 max-w-[160px]">
            <Dropdown
              value={currentWeek?.toString() || ""}
              onChange={(value) => onWeekChange(parseInt(value))}
              options={weeks}
              className="w-full"
            />
          </div>
        )}
      </div>

      {/* Full-bleed card */}
      <div className="flex-1 min-h-0 relative">
        <div
          ref={cardRef}
          className="absolute inset-x-3 top-0 bottom-2 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          style={{
            transform: `rotate(${cardRotation}deg) translateX(${cardTranslateX}px) translateY(${cardTranslateY}px)`,
            transformOrigin: 'center',
            transition: isAnimating ? 'transform 0.3s ease-out' : 'none',
            touchAction: 'pan-y',
          }}
          {...cardSwipeHandlers}
        >
          {/* Date / line strip */}
          <div className="flex-shrink-0 px-4 py-2 text-center border-b border-gray-100">
            <div className="text-xs text-gray-400">{formatGameDate(currentGame.gameDate)}</div>
            {currentGame.currentLine && (
              <div className="text-[11px] text-gray-400 mt-0.5">
                {currentGame.homeTeam.abbreviation} {currentGame.currentLine.homeSpread > 0 ? "+" : ""}
                {currentGame.currentLine.homeSpread}
              </div>
            )}
          </div>

          {/* Teams fill the rest of the card */}
          <div className="flex-1 min-h-0 flex relative">
            {/* Away Team - Left Side (swipe left) */}
            <div
              className="flex-1 min-w-0 flex flex-col items-center justify-center px-3 relative"
              onClick={() => canPickCurrent && handlePick(currentGame.awayTeam.abbreviation)}
            >
              {showAwayStamp && (
                <div
                  className="absolute top-6 left-2 border-4 border-red-500 text-red-500 font-black text-xl px-3 py-1 rounded-lg -rotate-12 pointer-events-none select-none"
                  style={{ opacity: dragStampStrength }}
                >
                  PASS
                </div>
              )}
              <div className="w-[38vw] h-[38vw] max-w-[150px] max-h-[150px] mb-4 relative flex-shrink-0">
                <CachedImage
                  src={currentGame.awayTeam.logoUrl || getTeamHelmetUrl(currentGame.awayTeam)}
                  fallbackSrc={getTeamHelmetUrl(currentGame.awayTeam)}
                  alt={currentGame.awayTeam.name}
                  className="w-full h-full"
                />
                {userPicks[currentGame.id] && currentGame.status === "completed" && (() => {
                  const isCorrect = getPickCorrectness(currentGame, userPicks[currentGame.id]);
                  const isSelected = userPicks[currentGame.id]?.selectedTeam?.abbreviation === currentGame.awayTeam.abbreviation;
                  return isSelected && isCorrect !== null ? (
                    <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center bg-white shadow-md">
                      {isCorrect ? (
                        <FiCheck className="w-4 h-4 text-green-600" />
                      ) : (
                        <FiX className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                  ) : null;
                })()}
              </div>
              <div className="text-[11px] text-gray-400 uppercase tracking-wide font-semibold mb-1">
                Away
              </div>
              <div className="text-xl font-bold text-gray-900 text-center leading-tight">
                {currentGame.awayTeam.city}
              </div>
              <div className="text-base text-gray-500 text-center">{currentGame.awayTeam.name}</div>
            </div>

            <div className="w-px bg-gray-100 flex-shrink-0" />

            {/* Home Team - Right Side (swipe right) */}
            <div
              className="flex-1 min-w-0 flex flex-col items-center justify-center px-3 relative"
              onClick={() => canPickCurrent && handlePick(currentGame.homeTeam.abbreviation)}
            >
              {showHomeStamp && (
                <div
                  className="absolute top-6 right-2 border-4 border-green-500 text-green-500 font-black text-xl px-3 py-1 rounded-lg rotate-12 pointer-events-none select-none"
                  style={{ opacity: dragStampStrength }}
                >
                  PICK
                </div>
              )}
              <div className="w-[38vw] h-[38vw] max-w-[150px] max-h-[150px] mb-4 relative flex-shrink-0">
                <CachedImage
                  src={currentGame.homeTeam.logoUrl || getTeamHelmetUrl(currentGame.homeTeam)}
                  fallbackSrc={getTeamHelmetUrl(currentGame.homeTeam)}
                  alt={currentGame.homeTeam.name}
                  className="w-full h-full"
                />
                {userPicks[currentGame.id] && currentGame.status === "completed" && (() => {
                  const isCorrect = getPickCorrectness(currentGame, userPicks[currentGame.id]);
                  const isSelected = userPicks[currentGame.id]?.selectedTeam?.abbreviation === currentGame.homeTeam.abbreviation;
                  return isSelected && isCorrect !== null ? (
                    <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center bg-white shadow-md">
                      {isCorrect ? (
                        <FiCheck className="w-4 h-4 text-green-600" />
                      ) : (
                        <FiX className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                  ) : null;
                })()}
              </div>
              <div className="text-[11px] text-gray-400 uppercase tracking-wide font-semibold mb-1">
                Home
              </div>
              <div className="text-xl font-bold text-gray-900 text-center leading-tight">
                {currentGame.homeTeam.city}
              </div>
              <div className="text-base text-gray-500 text-center">{currentGame.homeTeam.name}</div>
            </div>

            {/* VS badge, centered over the divider */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow-md border border-gray-100 flex items-center justify-center pointer-events-none">
              <span className="text-gray-400 font-bold text-xs">VS</span>
            </div>
          </div>

          {/* Subtle caption — only while the game can still be picked */}
          {canPickCurrent && (
            <div className="flex-shrink-0 text-center pb-3 pt-1">
              <span className="text-xs text-gray-400">
                {existingPickTeam
                  ? `Picked ${existingPickTeam} · swipe or tap to change`
                  : "Swipe or tap a team"}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Bottom action row — compact, floating circular buttons.
          The extra bottom padding lifts the buttons clear of any URL bar
          currently overlaying the page (see useAppHeight). */}
      {canPickCurrent && (
        <div
          className="flex-shrink-0 px-6 pt-1"
          style={{ paddingBottom: 'calc(1rem + var(--chrome-inset-bottom, 0px))' }}
        >
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={handleGoBack}
              disabled={currentGameIndex === 0 || isAnimating}
              className="w-11 h-11 rounded-full bg-white/10 disabled:opacity-30 flex items-center justify-center text-white shadow-lg touch-manipulation"
              title="Go back to previous game"
            >
              <FiRotateCcw className="w-4 h-4" />
            </button>

            <button
              onClick={() => handlePick(currentGame.awayTeam.abbreviation)}
              disabled={isAnimating || selectedTeam}
              className={`w-16 h-16 rounded-full bg-white shadow-xl flex items-center justify-center p-2 touch-manipulation disabled:opacity-50 ${
                highlightedTeam === currentGame.awayTeam.abbreviation ? "ring-4 ring-green-400" : ""
              }`}
              title={`Pick ${currentGame.awayTeam.abbreviation}`}
            >
              <CachedImage
                src={currentGame.awayTeam.logoUrl || getTeamHelmetUrl(currentGame.awayTeam)}
                fallbackSrc={getTeamHelmetUrl(currentGame.awayTeam)}
                alt={currentGame.awayTeam.name}
                className="w-full h-full"
              />
            </button>

            <button
              onClick={() => handlePick(currentGame.homeTeam.abbreviation)}
              disabled={isAnimating || selectedTeam}
              className={`w-16 h-16 rounded-full bg-white shadow-xl flex items-center justify-center p-2 touch-manipulation disabled:opacity-50 ${
                highlightedTeam === currentGame.homeTeam.abbreviation ? "ring-4 ring-green-400" : ""
              }`}
              title={`Pick ${currentGame.homeTeam.abbreviation}`}
            >
              <CachedImage
                src={currentGame.homeTeam.logoUrl || getTeamHelmetUrl(currentGame.homeTeam)}
                fallbackSrc={getTeamHelmetUrl(currentGame.homeTeam)}
                alt={currentGame.homeTeam.name}
                className="w-full h-full"
              />
            </button>

            <button
              onClick={handleSkipGame}
              disabled={isAnimating}
              className="w-11 h-11 rounded-full bg-white/10 disabled:opacity-30 flex items-center justify-center text-white shadow-lg touch-manipulation"
              title="Skip this game"
            >
              <FiChevronDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardBasedMobilePicks;
